/* Chef Akila – menu card behaviour (snippet ca-menu-card).
   Self-initialising and delegated: works for any number of cards, including cards added later
   (load more, carousels, theme editor). Keeps every card's "n in basket" stepper in step with the real cart. */
(() => {
  if (window.CAMenuCard) return;

  const SEL = '[data-ca-mcard]';
  document.documentElement.classList.add('ca-mcard-js');
  const OWN = { 'X-CA-Menu-Card': '1' };
  let qtyByVariant = new Map(); // variant id -> { qty, key }
  let cartLoaded = false;

  /* ---------- cart state ---------- */
  const readCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    const map = new Map();
    cart.items.forEach((item) => {
      const id = String(item.variant_id);
      const prev = map.get(id);
      if (prev) prev.qty += item.quantity;
      else map.set(id, { qty: item.quantity, key: item.key, lineQty: item.quantity });
    });
    qtyByVariant = map;
    cartLoaded = true;
    syncAll();
    updateBubble(cart.item_count);
  };

  const fetchCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json', ...OWN }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  const updateBubble = (count) => {
    if (typeof count !== 'number' || window.CACart) return; // the basket keeps the bag count (it leaves out the Saturday charge)
    document.querySelectorAll('.cart-count-bubble').forEach((b) => {
      const span = b.querySelector('span[aria-hidden]');
      if (span) span.textContent = count > 0 ? String(count) : '';
    });
  };

  // Hand the cart to the basket (assets/ca-cart.js): it re-renders, updates the bag count and tells every card
  const announce = async (cart) => {
    if (window.CACart && typeof window.CACart.refresh === 'function') {
      try { await window.CACart.refresh({ cart }); } catch (e) { /* basket refresh is best effort */ }
      return;
    }
    if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
  };

  // Every "Add to basket" goes through the basket's single API, which shows the "Added to your basket" feedback
  const addToBasket = async (id) => {
    if (window.CACart && typeof window.CACart.add === 'function') {
      await window.CACart.add([{ id: Number(id), quantity: 1 }], { source: 'card' });
      return null; // the basket announces the new cart (cart:updated) itself
    }
    await post('/cart/add.js', { items: [{ id: Number(id), quantity: 1 }] });
    return fetchCart();
  };

  /* ---------- card helpers ---------- */
  const sizeButtons = (card) => card.querySelectorAll('[data-ca-mcard-size]');
  const currentSize = (card) => {
    const id = card.dataset.variantId;
    return Array.from(sizeButtons(card)).find((b) => b.dataset.variantId === id) || sizeButtons(card)[0] || null;
  };

  const syncCard = (card) => {
    const size = currentSize(card);
    const id = card.dataset.variantId;
    let qty;
    if (cartLoaded) qty = qtyByVariant.get(id)?.qty || 0;
    else qty = Number(size?.dataset.qty || 0);
    const add = card.querySelector('[data-ca-mcard-add]');
    const stepper = card.querySelector('[data-ca-mcard-stepper]');
    if (!add || !stepper) return;
    const available = !size || size.dataset.available !== 'false';
    add.disabled = !available;
    add.hidden = qty > 0;
    stepper.hidden = qty <= 0;
    const q = card.querySelector('[data-ca-mcard-qty]');
    if (q) q.textContent = String(qty);
  };

  const syncAll = (root = document) => root.querySelectorAll(SEL).forEach(syncCard);

  const selectSize = (card, btn) => {
    sizeButtons(card).forEach((b) => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));
    card.dataset.variantId = btn.dataset.variantId;
    const weight = card.querySelector('[data-ca-mcard-weight]');
    if (weight) {
      weight.textContent = btn.dataset.weight || '';
      weight.hidden = !btn.dataset.weight;
      const wrap = card.querySelector('.ca-mcard__meta');
      if (wrap) wrap.hidden = !btn.dataset.weight && !card.querySelector('.ca-mcard__spice');
    }
    showError(card, '');
    syncCard(card);
  };

  const showError = (card, msg) => {
    const el = card.querySelector('[data-ca-mcard-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = !msg;
  };

  const busy = (el, on) => (on ? el.setAttribute('aria-busy', 'true') : el.removeAttribute('aria-busy'));

  const post = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...OWN },
      body: JSON.stringify(body),
    }).catch(() => { throw new Error('We couldn’t reach the basket. Please check your connection and try again.'); });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Sorry, something went wrong. Please try again.');
    return data;
  };

  /* ---------- printed-on photo labels ----------
     Many product photos already carry the label (green VEGAN / VEGETARIAN tag top right, gold BEST SELLER
     ribbon across the top-left corner). Read a 50×50 copy of the photo and hide the matching pill so the
     label doesn't show twice. Photos from another domain can't be read → the pill simply shows. */
  const PRINTED_N = 50;
  const isGold = (r, g, b) => r > 175 && r < 225 && g > 135 && g < 195 && b < 110 && r - g > 15 && r - g < 60 && g - b > 60;
  const isGreen = (r, g, b) => g > 130 && g - r > 40 && g - b > 25;
  const printedLabels = (img) => {
    const c = document.createElement('canvas');
    c.width = c.height = PRINTED_N;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, PRINTED_N, PRINTED_N);
    const d = ctx.getImageData(0, 0, PRINTED_N, PRINTED_N).data; // throws for cross-origin photos
    const test = (fn, i, j) => { const k = (j * PRINTED_N + i) * 4; return fn(d[k], d[k + 1], d[k + 2]); };
    let band = 0, bandN = 0, corner = 0, cornerN = 0, green = 0, greenN = 0;
    for (let i = 0; i < 16; i++) for (let j = 0; j < 16; j++) {
      const s = i + j;
      if (s >= 10 && s <= 13) { bandN++; if (test(isGold, i, j)) band++; }
      else if (s <= 5) { cornerN++; if (test(isGold, i, j)) corner++; }
    }
    for (let i = 40; i < 50; i++) for (let j = 3; j < 8; j++) { greenN++; if (test(isGreen, i, j)) green++; }
    return { ribbon: band / bandN >= 0.5 && corner / cornerN <= 0.2, tag: green / greenN >= 0.4 };
  };
  const checkBadge = (card) => {
    const badge = card.querySelector('[data-ca-mcard-badge]');
    if (!badge || badge.hasAttribute('data-checked')) return;
    const img = card.querySelector('.ca-mcard__img img');
    const done = (hide) => { badge.hidden = !!hide; badge.setAttribute('data-checked', hide ? 'printed' : ''); };
    if (!img) return done(false);
    const run = () => {
      if (!img.naturalWidth) return done(false);
      try {
        const found = printedLabels(img);
        done(badge.dataset.caMcardBadge === 'green' ? found.tag : found.ribbon);
      } catch (e) { done(false); }
    };
    if (img.complete) run();
    else {
      img.addEventListener('load', run, { once: true });
      img.addEventListener('error', () => done(false), { once: true });
    }
  };
  const checkBadges = (root = document) => root.querySelectorAll(SEL).forEach(checkBadge);

  /* ---------- actions ---------- */
  const add = async (card, btn) => {
    const id = Number(card.dataset.variantId);
    if (!id) return;
    busy(btn, true);
    showError(card, '');
    try {
      const cart = await addToBasket(id);
      if (cart) { readCart(cart); await announce(cart); }
    } catch (err) {
      showError(card, err.message);
    } finally {
      busy(btn, false);
    }
  };

  const step = async (card, delta) => {
    const id = card.dataset.variantId;
    const stepper = card.querySelector('[data-ca-mcard-stepper]');
    busy(stepper, true);
    showError(card, '');
    try {
      if (!cartLoaded) readCart(await fetchCart());
      const line = qtyByVariant.get(id);
      let cart;
      if (!line) {
        if (delta > 0) {
          cart = await addToBasket(id);
        }
      } else {
        // change only the first cart line for this variant (other lines, e.g. with properties, stay)
        cart = await post('/cart/change.js', { id: line.key, quantity: Math.max(0, line.lineQty + delta) });
      }
      if (cart) {
        readCart(cart);
        await announce(cart);
      }
    } catch (err) {
      showError(card, err.message);
      readCart(await fetchCart());
    } finally {
      busy(stepper, false);
    }
  };

  /* ---------- events ---------- */
  document.addEventListener('click', (e) => {
    const card = e.target.closest(SEL);
    if (!card) return;
    const size = e.target.closest('[data-ca-mcard-size]');
    if (size) {
      e.preventDefault();
      if (size.getAttribute('aria-disabled') === 'true') return;
      selectSize(card, size);
      return;
    }
    const addBtn = e.target.closest('[data-ca-mcard-add]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.getAttribute('aria-busy') === 'true' || addBtn.disabled) return;
      add(card, addBtn);
      return;
    }
    const stepBtn = e.target.closest('[data-ca-mcard-step]');
    if (stepBtn) {
      e.preventDefault();
      const stepper = stepBtn.closest('[data-ca-mcard-stepper]');
      if (stepper?.getAttribute('aria-busy') === 'true') return;
      step(card, Number(stepBtn.dataset.caMcardStep));
    }
  });

  // Size radios: arrow keys move the choice, like native radio buttons
  document.addEventListener('keydown', (e) => {
    const size = e.target.closest?.('[data-ca-mcard-size]');
    if (!size || !/^Arrow(Up|Down|Left|Right)$/.test(e.key)) return;
    const card = size.closest(SEL);
    const list = Array.from(sizeButtons(card)).filter((b) => b.getAttribute('aria-disabled') !== 'true');
    if (list.length < 2) return;
    e.preventDefault();
    const dir = e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 1;
    const next = list[(list.indexOf(size) + dir + list.length) % list.length];
    selectSize(card, next);
    next.focus();
  });

  // Cart changed somewhere else (drawer, product page form, other apps)
  document.addEventListener('cart:updated', (e) => {
    if (e.detail && Array.isArray(e.detail.items)) readCart(e.detail);
  });

  // Watch other scripts' cart requests (the drawer's own qty buttons don't always announce themselves)
  let refreshTimer;
  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => readCart(await fetchCart()), 250);
  };
  if (window.fetch && !window.fetch.__caMcard) {
    const nativeFetch = window.fetch;
    const wrapped = function (input, init) {
      const p = nativeFetch.apply(this, arguments);
      try {
        const url = typeof input === 'string' ? input : input && input.url;
        const headers = (init && init.headers) || {};
        const own = headers['X-CA-Menu-Card'] || (headers.get && headers.get('X-CA-Menu-Card'));
        if (!own && url && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) p.then(scheduleRefresh, () => {});
      } catch (err) { /* never break the caller */ }
      return p;
    };
    wrapped.__caMcard = true;
    window.fetch = wrapped;
  }

  /* ---------- init ---------- */
  const refresh = async () => readCart(await fetchCart());
  const init = () => {
    syncAll();
    checkBadges();
    if (document.querySelector(SEL)) refresh();
  };

  // New cards (load more, carousels, theme editor) get synced as they appear
  const mo = new MutationObserver((records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches?.(SEL)) { syncCard(n); checkBadge(n); }
        else n.querySelectorAll?.(SEL).forEach((c) => { syncCard(c); checkBadge(c); });
      }
    }
  });

  const start = () => {
    init();
    mo.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });

  window.CAMenuCard = { sync: syncAll, refresh };
})();
